import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Clock,
  Users,
  MapPin,
  Calendar,
  Settings,
  Star,
  TrendingUp,
  Activity,
  X,
  Camera,
  Image
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { compressImage } from '../../utils/imageUtils';

interface ClassType {
  id: string;
  name: string;
  description: string;
  color: string;
  image?: string; // URL de la imagen
  duration: number; // en minutos
  maxCapacity: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'all_levels';
  category: string;
  isActive: boolean;
  instructorIds: string[];
  schedule: {
    dayOfWeek: number; // 0 = domingo, 1 = lunes, etc.
    startTime: string;
    endTime: string;
    location: string;
    instructorId: string;
  }[];
  requirements?: string[];
  equipment?: string[];
  rating?: number; // Valoración media (1-5)
  totalRatings?: number; // Número total de valoraciones
  benefits?: string[];
  createdAt: Date;
  totalClasses: number;
  averageAttendance: number;
  rating: number;
  businessId: string;
}

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  specialties: string[];
  businessId: string;
}

interface ClassTypeFormData {
  name: string;
  description: string;
  color: string;
  duration: number;
  maxCapacity: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'all_levels';
  category: string;
  isActive: boolean;
  instructorIds: string[];
  requirements: string[];
  equipment: string[];
  benefits: string[];
}

const ClassTypesPage: React.FC = () => {
  const { businessId } = useBusiness();
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClassType, setSelectedClassType] = useState<ClassType | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [formData, setFormData] = useState<ClassTypeFormData>({
    name: '',
    description: '',
    color: '#3b82f6',
    duration: 60,
    maxCapacity: 15,
    difficulty: 'all_levels',
    category: '',
    isActive: true,
    instructorIds: [],
    requirements: [],
    equipment: [],
    benefits: []
  });

  // Estados para imagen
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cargar datos desde Firestore
  const loadData = async () => {
    if (!businessId) return;

    try {
      setLoading(true);

      // Cargar tipos de clase
      const classTypesQuery = query(collection(db, 'classTypes'), where('businessId', '==', businessId));
      const classTypesSnapshot = await getDocs(classTypesQuery);
      const classTypesData: ClassType[] = [];
      classTypesSnapshot.forEach((doc) => {
        classTypesData.push({ id: doc.id, ...doc.data() } as ClassType);
      });

      // Cargar instructores
      const instructorsQuery = query(collection(db, 'instructors'), where('businessId', '==', businessId));
      const instructorsSnapshot = await getDocs(instructorsQuery);
      const instructorsData: Instructor[] = [];
      instructorsSnapshot.forEach((doc) => {
        instructorsData.push({ id: doc.id, ...doc.data() } as Instructor);
      });

      setClassTypes(classTypesData);
      setInstructors(instructorsData);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      loadData();
    }
  }, [businessId]);

  const handleCreateClassType = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3b82f6',
      duration: 60,
      maxCapacity: 15,
      difficulty: 'all_levels',
      category: '',
      isActive: true,
      instructorIds: [],
      requirements: [],
      equipment: [],
      benefits: []
    });
    setImageFile(null);
    setImagePreview('');
    setShowCreateModal(true);
  };

  const handleEditClassType = (classType: ClassType) => {
    setSelectedClassType(classType);
    setFormData({
      name: classType.name,
      description: classType.description,
      color: classType.color,
      duration: classType.duration,
      maxCapacity: classType.maxCapacity,
      difficulty: classType.difficulty,
      category: classType.category,
      isActive: classType.isActive,
      instructorIds: classType.instructorIds || [],
      requirements: classType.requirements || [],
      equipment: classType.equipment || [],
      benefits: classType.benefits || []
    });
    setImageFile(null);
    setImagePreview(classType.image || '');
    setShowEditModal(true);
  };

  // Manejar selección de imagen
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteClassType = async (classTypeId: string) => {
    if (confirm('¿Estás seguro de que quieres eliminar este tipo de clase?')) {
      try {
        await deleteDoc(doc(db, 'classTypes', classTypeId));
        toast.success('Tipo de clase eliminado exitosamente');
        loadData();
      } catch (error) {
        console.error('Error eliminando tipo de clase:', error);
        toast.error('Error al eliminar el tipo de clase');
      }
    }
  };

  const createClassType = async () => {
    if (!businessId) return;

    try {
      if (!formData.name || !formData.description) {
        toast.error('Por favor completa los campos obligatorios');
        return;
      }

      // Subir imagen si se seleccionó
      let imageUrl: string | undefined = undefined;
      if (imageFile) {
        const compressedFile = await compressImage(imageFile);
        const path = `classTypes/${businessId}/${Date.now()}_${compressedFile.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, compressedFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      const newClassType = {
        ...formData,
        image: imageUrl,
        schedule: [],
        totalClasses: 0,
        averageAttendance: 0,
        rating: 0,
        businessId, // Agregar businessId
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'classTypes'), newClassType);
      toast.success('Clase creada exitosamente');
      setShowCreateModal(false);
      setImageFile(null);
      setImagePreview('');
      loadData();
    } catch (error) {
      console.error('Error creando clase:', error);
      toast.error('Error al crear la clase');
    }
  };

  const updateClassType = async () => {
    try {
      if (!selectedClassType) return;

      if (!formData.name || !formData.description) {
        toast.error('Por favor completa los campos obligatorios');
        return;
      }

      // Subir nueva imagen si se seleccionó
      let imageUrl = selectedClassType.image || undefined;
      if (imageFile) {
        const compressedFile = await compressImage(imageFile);
        const path = `classTypes/${businessId}/${Date.now()}_${compressedFile.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, compressedFile);
        imageUrl = await getDownloadURL(storageRef);
      } else if (!imagePreview) {
        // Si se eliminó la imagen
        imageUrl = undefined;
      }

      const updatedClassType = {
        ...formData,
        image: imageUrl,
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, 'classTypes', selectedClassType.id), updatedClassType);
      toast.success('Tipo de clase actualizado exitosamente');
      setShowEditModal(false);
      setSelectedClassType(null);
      loadData();
    } catch (error) {
      console.error('Error actualizando tipo de clase:', error);
      toast.error('Error al actualizar el tipo de clase');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3b82f6',
      duration: 60,
      maxCapacity: 15,
      difficulty: 'all_levels',
      category: '',
      isActive: true,
      instructorIds: [],
      requirements: [],
      equipment: [],
      benefits: []
    });
  };

  // Función temporal para limpiar datos ficticios
  const cleanupFakeData = async () => {
    if (!businessId) return;

    try {
      console.log('🧹 Iniciando limpieza de datos ficticios...');

      // Eliminar tipos de clase ficticios
      const classTypesQuery = query(collection(db, 'classTypes'), where('businessId', '==', businessId));
      const classTypesSnapshot = await getDocs(classTypesQuery);
      const fakeClassTypes = ['Kickboxing', 'Jiu-Jitsu Brasileño', 'Muay Thai', 'Karate Básico', 'Karate Avanzado', 'Defensa Personal'];

      for (const doc of classTypesSnapshot.docs) {
        const data = doc.data();
        if (fakeClassTypes.includes(data.name)) {
          console.log(`🗑️ Eliminando tipo de clase: ${data.name}`);
          await deleteDoc(doc.ref);
        }
      }

      // Eliminar instructores ficticios
      const instructorsQuery = query(collection(db, 'instructors'), where('businessId', '==', businessId));
      const instructorsSnapshot = await getDocs(instructorsQuery);
      const fakeInstructors = ['Carlos', 'Ana', 'Miguel', 'Sensei', 'Maestra', 'Profesor'];

      for (const doc of instructorsSnapshot.docs) {
        const data = doc.data();
        if (fakeInstructors.includes(data.firstName) || fakeInstructors.includes(data.lastName)) {
          console.log(`🗑️ Eliminando instructor: ${data.firstName} ${data.lastName}`);
          await deleteDoc(doc.ref);
        }
      }

      // Eliminar clases ficticias
      const classesQuery = query(collection(db, 'classes'), where('businessId', '==', businessId));
      const classesSnapshot = await getDocs(classesQuery);
      const fakeClassNames = ['Kickboxing', 'Jiu-Jitsu', 'Muay Thai', 'Karate', 'Defensa Personal'];

      for (const doc of classesSnapshot.docs) {
        const data = doc.data();
        if (fakeClassNames.some(name => data.name.includes(name))) {
          console.log(`🗑️ Eliminando clase: ${data.name}`);
          await deleteDoc(doc.ref);
        }
      }

      toast.success('Datos ficticios eliminados exitosamente');
      loadData(); // Recargar datos
    } catch (error) {
      console.error('❌ Error durante la limpieza:', error);
      toast.error('Error al limpiar datos ficticios');
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'Principiante';
      case 'intermediate': return 'Intermedio';
      case 'advanced': return 'Avanzado';
      case 'all_levels': return 'Todos los niveles';
      default: return difficulty;
    }
  };

  const getDayName = (dayOfWeek: number) => {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[dayOfWeek];
  };

  const filteredClassTypes = classTypes.filter(classType => {
    if (filter === 'active') return classType.isActive;
    if (filter === 'inactive') return !classType.isActive;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando tipos de clase...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Clases
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Administra las diferentes disciplinas y sus configuraciones
          </p>
        </div>
        <Button onClick={handleCreateClassType}>
          <Plus className="h-4 w-4 mr-2" />
          Añadir Clase
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex space-x-2">
        <Button
          variant={filter === 'all' ? 'primary' : 'outline'}
          onClick={() => setFilter('all')}
        >
          Todos ({classTypes.length})
        </Button>
        <Button
          variant={filter === 'active' ? 'primary' : 'outline'}
          onClick={() => setFilter('active')}
        >
          Activas ({classTypes.filter(ct => ct.isActive).length})
        </Button>
        <Button
          variant={filter === 'inactive' ? 'primary' : 'outline'}
          onClick={() => setFilter('inactive')}
        >
          Inactivas ({classTypes.filter(ct => !ct.isActive).length})
        </Button>
      </div>

      {/* Lista de Tipos de Clase */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredClassTypes.map((classType) => (
          <div
            key={classType.id}
            className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-primary-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/10"
          >
            {/* Imagen de la clase */}
            <div className="relative h-40 overflow-hidden">
              {classType.image ? (
                <img
                  src={classType.image}
                  alt={classType.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${classType.color}40 0%, ${classType.color}20 100%)`
                  }}
                >
                  <Activity className="h-16 w-16 text-gray-500" />
                </div>
              )}
              {/* Overlay con gradiente */}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent" />

              {/* Badges en la imagen */}
              <div className="absolute top-3 left-3 flex gap-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full backdrop-blur-sm ${classType.isActive
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                  {classType.isActive ? 'Activa' : 'Inactiva'}
                </span>
              </div>

              {/* Botones de acción */}
              <div className="absolute top-3 right-3 flex gap-1">
                <button
                  onClick={() => handleEditClassType(classType)}
                  className="p-2 rounded-lg bg-gray-900/60 backdrop-blur-sm text-white hover:bg-primary-500 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteClassType(classType.id)}
                  className="p-2 rounded-lg bg-gray-900/60 backdrop-blur-sm text-white hover:bg-red-500 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {/* Nombre sobre la imagen */}
              <div className="absolute bottom-3 left-3 right-3">
                <h3 className="text-xl font-bold text-white drop-shadow-lg">
                  {classType.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getDifficultyColor(classType.difficulty)}`}>
                    {getDifficultyText(classType.difficulty)}
                  </span>
                  {classType.category && (
                    <span className="text-xs text-gray-300">
                      {classType.category}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Contenido de la tarjeta */}
            <div className="p-4">
              {/* Descripción */}
              <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                {classType.description}
              </p>

              {/* Info rápida */}
              <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-primary-400" />
                  <span>{classType.duration} min</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4 text-primary-400" />
                  <span>Máx {classType.maxCapacity}</span>
                </div>
              </div>

              {/* Estadísticas */}
              <div className="grid grid-cols-3 gap-2 p-3 bg-gray-900/50 rounded-lg">
                <div className="text-center">
                  <div className="text-lg font-bold text-white">
                    {classType.totalClasses || 0}
                  </div>
                  <div className="text-xs text-gray-500">
                    Clases
                  </div>
                </div>
                <div className="text-center border-x border-gray-700">
                  <div className="text-lg font-bold text-white">
                    {classType.averageAttendance || 0}
                  </div>
                  <div className="text-xs text-gray-500">
                    Asistencia
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-white flex items-center justify-center gap-1">
                    <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                    {(classType.rating || 0).toFixed(1)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {classType.totalRatings || 0} votos
                  </div>
                </div>
              </div>

              {/* Horarios */}
              {classType.schedule && classType.schedule.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Horarios
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {classType.schedule.slice(0, 3).map((schedule, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded"
                      >
                        {getDayName(schedule.dayOfWeek).slice(0, 3)} {schedule.startTime}
                      </span>
                    ))}
                    {classType.schedule.length > 3 && (
                      <span className="px-2 py-1 text-xs bg-gray-700 text-gray-400 rounded">
                        +{classType.schedule.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Añadir Clase */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 p-6 rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Añadir Nueva Clase</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Nombre *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nombre del tipo de clase"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Categoría
                  </label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="Ej: Striking, Grappling"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-400"
                  rows={3}
                  placeholder="Descripción detallada de la clase..."
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Duración
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value={30} className="bg-gray-800 text-white">30 minutos</option>
                    <option value={60} className="bg-gray-800 text-white">1 hora</option>
                    <option value={90} className="bg-gray-800 text-white">1h 30min</option>
                    <option value={120} className="bg-gray-800 text-white">2 horas</option>
                    <option value={150} className="bg-gray-800 text-white">2h 30min</option>
                    <option value={180} className="bg-gray-800 text-white">3 horas</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Capacidad Máxima
                  </label>
                  <Input
                    type="number"
                    value={formData.maxCapacity}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxCapacity: parseInt(e.target.value) || 15 }))}
                    min="1"
                    max="50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Dificultad
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white"
                    value={formData.difficulty}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value as any }))}
                  >
                    <option value="beginner" className="bg-gray-800 text-white">Principiante</option>
                    <option value="intermediate" className="bg-gray-800 text-white">Intermedio</option>
                    <option value="advanced" className="bg-gray-800 text-white">Avanzado</option>
                    <option value="all_levels" className="bg-gray-800 text-white">Todos los niveles</option>
                  </select>
                </div>
              </div>

              {/* Imagen de la clase */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Imagen de la clase
                </label>
                <div className="flex items-start gap-4">
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-32 h-24 object-cover rounded-lg border-2 border-gray-600"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-32 h-24 rounded-lg bg-gray-700 border-2 border-dashed border-gray-600 flex items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-gray-600 transition-colors"
                      >
                        <div className="text-center">
                          <Image className="w-8 h-8 text-gray-400 mx-auto" />
                          <span className="text-xs text-gray-400 mt-1 block">Añadir</span>
                        </div>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-400">
                      Esta imagen se mostrará en el catálogo de clases.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Formatos: JPG, PNG.
                    </p>
                  </div>
                </div>
              </div>

              {/* Estado */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Estado
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white"
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'active' }))}
                >
                  <option value="active" className="bg-gray-800 text-white">Activa</option>
                  <option value="inactive" className="bg-gray-800 text-white">Inactiva</option>
                </select>
              </div>

              {/* Selector de instructores */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Instructores que imparten esta clase
                </label>
                {instructors.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">
                    No hay instructores creados. Crea instructores primero.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-600 rounded-lg bg-gray-700/50 max-h-32 overflow-y-auto">
                    {instructors.map(instructor => (
                      <button
                        key={instructor.id}
                        type="button"
                        onClick={() => {
                          const newIds = formData.instructorIds.includes(instructor.id)
                            ? formData.instructorIds.filter(id => id !== instructor.id)
                            : [...formData.instructorIds, instructor.id];
                          setFormData(prev => ({ ...prev, instructorIds: newIds }));
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${formData.instructorIds.includes(instructor.id)
                          ? 'bg-primary-500 text-white shadow-md'
                          : 'bg-gray-600 text-gray-300 border border-gray-500 hover:border-primary-400'
                          }`}
                      >
                        {instructor.firstName} {instructor.lastName}
                      </button>
                    ))}
                  </div>
                )}
                {formData.instructorIds.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {formData.instructorIds.length} instructor(es) seleccionado(s)
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={createClassType}
                disabled={!formData.name || !formData.description}
              >
                Añadir Clase
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar Tipo de Clase */}
      {showEditModal && selectedClassType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 p-6 rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">
                Editar Tipo de Clase: {selectedClassType.name}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Nombre *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nombre del tipo de clase"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Categoría
                  </label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="Ej: Striking, Grappling"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-400"
                  rows={3}
                  placeholder="Descripción detallada de la clase..."
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Duración
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value={30} className="bg-gray-800 text-white">30 minutos</option>
                    <option value={60} className="bg-gray-800 text-white">1 hora</option>
                    <option value={90} className="bg-gray-800 text-white">1h 30min</option>
                    <option value={120} className="bg-gray-800 text-white">2 horas</option>
                    <option value={150} className="bg-gray-800 text-white">2h 30min</option>
                    <option value={180} className="bg-gray-800 text-white">3 horas</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Capacidad Máxima
                  </label>
                  <Input
                    type="number"
                    value={formData.maxCapacity}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxCapacity: parseInt(e.target.value) || 15 }))}
                    min="1"
                    max="50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Dificultad
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white"
                    value={formData.difficulty}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value as any }))}
                  >
                    <option value="beginner" className="bg-gray-800 text-white">Principiante</option>
                    <option value="intermediate" className="bg-gray-800 text-white">Intermedio</option>
                    <option value="advanced" className="bg-gray-800 text-white">Avanzado</option>
                    <option value="all_levels" className="bg-gray-800 text-white">Todos los niveles</option>
                  </select>
                </div>
              </div>

              {/* Imagen de la clase */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Imagen de la clase
                </label>
                <div className="flex items-start gap-4">
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-32 h-24 object-cover rounded-lg border-2 border-gray-600"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-32 h-24 rounded-lg bg-gray-700 border-2 border-dashed border-gray-600 flex items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-gray-600 transition-colors"
                      >
                        <div className="text-center">
                          <Image className="w-8 h-8 text-gray-400 mx-auto" />
                          <span className="text-xs text-gray-400 mt-1 block">Añadir</span>
                        </div>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-400">
                      Esta imagen se mostrará en el catálogo de clases.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Formatos: JPG, PNG.
                    </p>
                  </div>
                </div>
              </div>

              {/* Estado */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Estado
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white"
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'active' }))}
                >
                  <option value="active" className="bg-gray-800 text-white">Activa</option>
                  <option value="inactive" className="bg-gray-800 text-white">Inactiva</option>
                </select>
              </div>

              {/* Selector de instructores */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Instructores que imparten esta clase
                </label>
                {instructors.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">
                    No hay instructores creados. Crea instructores primero.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-600 rounded-lg bg-gray-700/50 max-h-32 overflow-y-auto">
                    {instructors.map(instructor => (
                      <button
                        key={instructor.id}
                        type="button"
                        onClick={() => {
                          const newIds = formData.instructorIds.includes(instructor.id)
                            ? formData.instructorIds.filter(id => id !== instructor.id)
                            : [...formData.instructorIds, instructor.id];
                          setFormData(prev => ({ ...prev, instructorIds: newIds }));
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${formData.instructorIds.includes(instructor.id)
                          ? 'bg-primary-500 text-white shadow-md'
                          : 'bg-gray-600 text-gray-300 border border-gray-500 hover:border-primary-400'
                          }`}
                      >
                        {instructor.firstName} {instructor.lastName}
                      </button>
                    ))}
                  </div>
                )}
                {formData.instructorIds.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {formData.instructorIds.length} instructor(es) seleccionado(s)
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={updateClassType}
                disabled={!formData.name || !formData.description}
              >
                Guardar Cambios
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassTypesPage; 